import 'core-js/stable';

/* eslint-disable no-restricted-globals */
import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import configureStore from './store';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import serviceContainer from './services/serviceContainer';
import { appService } from './services/appService';
import OfficeEventService from './services/officeEventService';
import { localSettingsService } from './services/localSettingsService';
import { DynamicScriptService } from './services/dynamicScriptService';
import { OfficeApi } from './office/OfficeApi';
import { performanceTimingService, PERFORMANCE_EVENT } from './services/performanceTimingService';

type OfficeBootstrap = {
    onReady: (callback?: () => void) => Promise<unknown>;
};

type AddinCommandGlobal = typeof globalThis & {
    processOnSend?: typeof processOnSend;
    documentOpened?: typeof documentOpened;
};

//Application Startup: Get and initialize officeJs from settings & render application
localSettingsService.loadLocalSettings().then(() => {
    const officeJsLocation = localSettingsService.getLocalSettings().officeJsLocation;
    const officeJsSriHash = localSettingsService.getLocalSettings().officeJsSriHash;
    const officeJsUrl = new URL(officeJsLocation, window.location.href);

    // MS nullify the history API as it doesnt work in IE10 based Excel (see discussion here https://github.com/OfficeDev/office-js/issues/429)
    // suggested workaround if thats not an issue is to cache and restore the API as seen here (https://stackoverflow.com/a/53662709)
    const historyCache = {
        replaceState: window.history.replaceState,
        pushState: window.history.pushState,
    };

    // Outlook's host-specific Office.js file otherwise loads MicrosoftAjax.js from Microsoft's CDN.
    // When Office.js is hosted with the application, preload our separately maintained local copy.
    const isLocallyHostedOfficeJs = officeJsUrl.origin === window.location.origin;
    const microsoftAjaxLocation = new URL('../MicrosoftAjaxFiles/MicrosoftAjax.js', officeJsUrl).href;
    const officeJsLoadPromise = isLocallyHostedOfficeJs
        ? DynamicScriptService.loadExternalResource(microsoftAjaxLocation, undefined).then(() =>
              DynamicScriptService.loadExternalResource(officeJsUrl.href, officeJsSriHash),
          )
        : DynamicScriptService.loadExternalResource(officeJsUrl.href, officeJsSriHash);

    officeJsLoadPromise.then(() => {
        const office = (window as Window & { Office?: OfficeBootstrap }).Office;
        if (!office) {
            return;
        }
        // Office js deletes window.history.pushState and window.history.replaceState. Restore them
        window.history.replaceState = historyCache.replaceState;
        window.history.pushState = historyCache.pushState;

        office.onReady().then(() => {
            //Initialise our OfficeApi and children when Office is ready
            serviceContainer.getService<OfficeApi>('OfficeApi').initialise();
            associateDocumentOpenedHandler();

            const container = document.getElementById('root');
            //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const root = createRoot(container!);
            root.render(
                <Provider store={store}>
                    <Router>
                        <App />
                    </Router>
                </Provider>,
            );
        });
    });
});

const store = configureStore({
    readPaneReducer: {
        isLoading: false,
        error: null,
        loadingFieldCount: 0,
        classificationPaneViewModel: null,
    },
    selectClassificationReducer: {
        processing: false,
        processingMessage: null,
        error: null,
        classificationPaneViewModel: null,
        applicationSettings: null,
        suggestPreamble: null,
        themeColor: '',
    },
    alertsReducer: {
        processing: false,
        processingMessage: null,
        error: null,
        alerts: [],
        mode: undefined,
        canRemediateAll: false,
        remediated: false,
        isHostOutlook: false,
        themeColor: '',
        applicationSettings: null,
    },
});

initializeIcons('/static/fonts/');

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

export const getGlobal = () => {
    return typeof self !== 'undefined'
        ? self
        : typeof window !== 'undefined'
          ? window
          : typeof global !== 'undefined'
            ? global
            : undefined;
};

const processOnSend = async (event: Office.AddinCommands.Event) => {
    await localSettingsService.loadLocalSettings();
    performanceTimingService.recordEvent(PERFORMANCE_EVENT.SEND_PROCESSING_START);
    try {
        const officeEventService = new OfficeEventService(appService, serviceContainer);
        const allowOnSend = await officeEventService.evaluateOnSend();
        performanceTimingService.recordEvent(PERFORMANCE_EVENT.SEND_PROCESSINGS_COMPLETE);
        event.completed({ allowEvent: allowOnSend });
    } catch {
        performanceTimingService.recordEvent(PERFORMANCE_EVENT.SEND_PROCESSINGS_COMPLETE);
        event.completed({ allowEvent: true });
    }
};

const documentOpened = async (event: Office.AddinCommands.Event) => {
    try {
        await localSettingsService.loadLocalSettings();
        serviceContainer.getService<OfficeApi>('OfficeApi').initialise();

        const officeEventService = new OfficeEventService(appService, serviceContainer);
        await officeEventService.evaluateOnDocumentOpened();
    } catch (error) {
        console.error('DCS One document-open processing failed.', error);
    } finally {
        event.completed();
    }
};

let isDocumentOpenedHandlerAssociated = false;
const associateDocumentOpenedHandler = () => {
    if (!isDocumentOpenedHandlerAssociated && typeof Office !== 'undefined' && Office.actions) {
        Office.actions.associate('documentOpened', documentOpened);
        isDocumentOpenedHandlerAssociated = true;
    }
};

const glob = getGlobal() as AddinCommandGlobal | undefined;

// the add-in command functions need to be available in global scope
if (glob) {
    glob.processOnSend = processOnSend;
    glob.documentOpened = documentOpened;
}
